<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

import CreateOrUpdateDialog from '@/components/dialog/CreateOrUpdateDialog.vue'
import { useSpeckleManagement } from '@/composables/useSpeckleManagement'
import TableButtonGroup from '@/components/calculation-index/TableButtonGroup.vue'
import CalculationTable from '@/components/calculation-index/CalculationTable.vue'
import BulkActionDialog from '@/components/dialog/BulkActionDialog.vue'

import { useCalculationStore } from '@/stores/calculation'
import { useProjectSpeckleStore } from '@/stores/projectSpeckle'

import { BERTH_TYPE, MOORING_SIDE, SHIP_TYPE } from '@/constants/calculationFieldConstants'

const route = useRoute()
const calculationStore = useCalculationStore()
const projectSpeckleStore = useProjectSpeckleStore()

const speckleManagement = useSpeckleManagement()

const { currentItem: currentProjectSpeckle } = storeToRefs(projectSpeckleStore)
const { items, selectedItems, deleteLoading, deleteDialog, writeLoading, duplicateDialog } =
  storeToRefs(calculationStore)

onMounted(async () => {
  await calculationStore.fetchOptions()
  await calculationStore.fetchItems({ project: route.params.id })
})

const searchName = ref('')
const berthType = ref('')
const shipType = ref('')
const mooringSide = ref('')

const updateFilterParams = async (payload) => {
  calculationStore.emptySelectedItems()
  if (payload.searchName !== undefined) {
    searchName.value = payload.searchName
  }
  if (payload.berthType !== undefined) {
    berthType.value = payload.berthType
  }
  if (payload.shipType !== undefined) {
    shipType.value = payload.shipType
  }
  if (payload.mooringSide !== undefined) {
    mooringSide.value = payload.mooringSide
  }
}

const filteredItems = computed(() => {
  return items.value.filter((item) => {
    return (
      item.name.toLowerCase().includes(searchName.value.toLowerCase()) &&
      (!berthType.value || item[BERTH_TYPE] === berthType.value) &&
      (!shipType.value || item[SHIP_TYPE] === shipType.value) &&
      (!mooringSide.value || item[MOORING_SIDE] === mooringSide.value)
    )
  })
})

async function postOrUpdate() {
  const response = await calculationStore.allAction()
  if (response) {
    const speckleProjectId = currentProjectSpeckle.value?.project_id
    if (speckleProjectId) {
      await speckleManagement.createAndLinkDefaultModel(speckleProjectId, response)
    }
    calculationStore.toggleCreateUpdateDialog()
  }
}

const deleteItems = async () => {
  await calculationStore.bulkDelete(selectedItems.value)
  calculationStore.toggleDeleteDialog()
  calculationStore.emptySelectedItems()
}

const duplicateItems = async () => {
  await calculationStore.bulkDuplicate(selectedItems.value)
  calculationStore.toggleDuplicateDialog()
  calculationStore.emptySelectedItems()
}
</script>
<template>
  <v-card border="opacity-35 sm" variant="text">
    <TableButtonGroup
      :filtered-items="filteredItems"
      :search-name="searchName"
      :berth-type="berthType"
      :ship-type="shipType"
      :mooring-side="mooringSide"
      @update-filter-params="updateFilterParams"
    />
    <CalculationTable :filtered-items="filteredItems" />
    <BulkActionDialog
      :bulk-action="true"
      :items-to-action="selectedItems"
      :loading="deleteLoading"
      :dialog="deleteDialog"
      @toggle-dialog="calculationStore.toggleDeleteDialog"
      @action="deleteItems"
    ></BulkActionDialog>

    <BulkActionDialog
      :bulk-action="true"
      action-text="Duplicate"
      bulk-action-text="Duplicate these {amount} calculations?"
      button-color="primary"
      :items-to-action="selectedItems"
      :loading="writeLoading"
      :dialog="duplicateDialog"
      @toggle-dialog="calculationStore.toggleDuplicateDialog"
      @action="duplicateItems"
    ></BulkActionDialog>

    <CreateOrUpdateDialog
      :store="calculationStore"
      name="calculation"
      @save="postOrUpdate()"
    ></CreateOrUpdateDialog>
  </v-card>
</template>
